import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import InviteMemberModal from "./InviteMemberModal";
import { HiDotsHorizontal } from "react-icons/hi";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import { companyService } from "@services/CompanyService";
import Loader from "@shared/components/Loader";
import { toastService } from "@services/ToastService";
import { useUser } from "@services/UserContext";
import { useNavigate } from "react-router-dom";
import DeleteEntityModal from "@pages/Program/components/DeleteEntity";
import Pill from "@shared/components/Pill";

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  status: "approved" | "rejected" | "disabled" | "pending" | string;
  invited_by?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

const statusColorMap: Record<string, string> = {
  approved: "text-green-700 bg-green-50 border-green-700",
  rejected: "text-red-700 bg-red-50 border-red-700",
  disabled: "text-gray-700 bg-gray-50 border-gray-700",
  pending: "text-yellow-600 bg-yellow-50 border-yellow-500",
};

const ManageMembers: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [recentlyApprovedId, setRecentlyApprovedId] = useState<number | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [selectedEntity, setSelectedEntity] = useState<Member | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!user?.company_relation?.is_admin) {
      navigate("/assistant");
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCompanyMembers();
      setMembers(data || []);
    } catch (err) {
      toastService.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      setProcessingId(id);
      await companyService.approveUser(id);
      setRecentlyApprovedId(id);
      toastService.success("User approved successfully");
      fetchMembers();
    } catch {
      toastService.error("Failed to approve user");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: number) => {
    try {
      setProcessingId(id);
      await companyService.rejectUser(id);
      toastService.success("User rejected successfully");
      fetchMembers();
    } catch {
      toastService.error("Failed to reject user");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteMember = async () => {
    if (!selectedEntity) return;
    try {
      setProcessingId(selectedEntity.id);
      await companyService.deleteUser(selectedEntity.id);
      toastService.success("User deactivated successfully");
      fetchMembers();
    } catch {
      toastService.error("Failed to deactivate user");
    } finally {
      setProcessingId(null);
      setModalOpen(false);
      setSelectedEntity(null);
    }
  };

  const handleMenuToggle = (event: React.MouseEvent, member: Member) => {
    if (selectedEntity?.id === member.id) {
      setSelectedEntity(null);
      setMenuPosition(null);
    } else {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      setSelectedEntity(member);
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 100,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setSelectedEntity(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredMembers = useMemo(() => {
    const ordered = members;

    if (!search.trim()) return ordered;

    const q = search.toLowerCase();
    return ordered.filter((member) => {
      const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
      return fullName.includes(q) || member.email.toLowerCase().includes(q);
    });
  }, [search, members, recentlyApprovedId]);

  return (
    <>
      <div className="px-8 pb-[0.67rem] 2xl:pb-3 flex-1 flex flex-col">
        <SubTitleHeader
          title="Manage Members"
          subtitleStyle="text-gray-400 text-sm 2xl:text-base"
        />

        <div className="2xl:mt-6 mt-4 flex flex-col flex-1">
          <div className="border border-gray-200 rounded-md bg-white relative">
            <div className="flex justify-between items-center bg-[#F9FAFB] px-4 py-3 2xl:py-4 rounded-md">
              <div className="text-base 2xl:text-lg font-semibold">
                Overview{" "}
                <span className="text-gray-500 font-normal text-sm 2xl:text-base">
                  ({members.length} members)
                </span>
              </div>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border rounded px-3 py-1.5 text-sm w-64"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-10">
                <Loader color="#0056C8" />
              </div>
            ) : (
              <div className="max-h-[30rem] 2xl:max-h-[36rem] overflow-y-auto overflow-x-hidden custom-scroll">
                <div className="grid grid-cols-[1.5fr_1.8fr_1fr_2fr_1.5fr_0.3fr] text-sm font-medium p-4 text-gray-600 border-b sticky top-0 bg-white z-10">
                  <div>
                    <div className="flex items-center space-x-2">Name</div>
                  </div>
                  <div>Email Address</div>
                  <div className="text-center">Status</div>
                  <div>Invited By</div>
                  <div>Action</div>
                  <div />
                </div>

                {filteredMembers.length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    No members found
                  </div>
                )}

                {filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className="grid grid-cols-[1.5fr_1.8fr_1fr_2fr_1.5fr_0.3fr] items-center text-sm p-4 py-3 border-b hover:bg-gray-50 relative"
                  >
                    <div>
                      {member.first_name} {member.last_name}
                    </div>
                    <div className="">{member.email}</div>
                    <div className="flex items-center space-x-2">
                      <Pill
                        styling={`mx-auto min-w-[5.5rem] max-w-[7rem] text-center ${
                          statusColorMap[member.status] ||
                          "text-gray-700 bg-gray-50 border-gray-700 "
                        }`}
                        text={
                          member.status === "approved"
                            ? "Active"
                            : member.status === "disabled"
                            ? "Deactivated"
                            : member.status.charAt(0).toUpperCase() +
                              member.status.slice(1)
                        }
                      />
                    </div>
                    <div>{member.invited_by && member.invited_by.email}</div>
                    <div>
                      {member.status === "pending" ? (
                        <div className="flex gap-2 ">
                          <button
                            onClick={() => handleApprove(member.id)}
                            disabled={processingId === member.id}
                            className="bg-[#EAFBF2] text-green-700 border border-green-700 px-4 py-1 rounded text-xs hover:bg-green-100 disabled:opacity-50"
                          >
                            {processingId === member.id ? "..." : "Approve"}
                          </button>
                          <button
                            onClick={() => handleReject(member.id)}
                            disabled={processingId === member.id}
                            className="bg-[#FFFBFA] text-red-700 border border-red-700 px-4 py-1 rounded text-xs hover:bg-red-100 disabled:opacity-50"
                          >
                            {processingId === member.id ? "..." : "Decline"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs"></span>
                      )}
                    </div>
                    <div className="flex justify-end pr-2 relative">
                      {member.status !== "pending" && (
                        <button onClick={(e) => handleMenuToggle(e, member)}>
                          <HiDotsHorizontal className="text-gray-500 hover:text-gray-700 w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Portal for context menu */}
      {selectedEntity &&
        menuPosition &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="fixed z-50 w-32 bg-white border border-gray-200 rounded shadow"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {selectedEntity.status === "approved" ? (
              <button
                onClick={() => {
                  setModalOpen(true);
                  setMenuPosition(null);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
              >
                Deactivate
              </button>
            ) : (
              (selectedEntity.status === "rejected" ||
                selectedEntity.status === "disabled") && (
                <button
                  onClick={() => {
                    handleApprove(selectedEntity.id);
                    setMenuPosition(null);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-gray-100"
                >
                  Activate
                </button>
              )
            )}
          </div>,
          document.body
        )}

      <DeleteEntityModal
        isOpen={modalOpen}
        selectedEntity={{
          name: selectedEntity ? `${selectedEntity.email}` : "",
        }}
        onClose={() => setModalOpen(false)}
        onConfirm={handleDeleteMember}
        titleText="Are you sure you want to deactivate"
        confirmText="Deactivate"
        detailsText="This action will deactivate this member."
      />

      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </>
  );
};

export default ManageMembers;
